import { useState } from 'react'
import { useLanguage } from '@context/LanguageContext'
import { useDeviceType } from '@hooks/useDeviceType'
import { useSwipe } from '@hooks/useSwipe'
import GameCard from './GameCard'
import { GAMES_INFO } from '@constants/games'
import styles from './GameGallery.module.css'

/**
 * GameGallery - Container for game selection cards with responsive layout
 *
 * @param {Object} props
 * @param {Array} [props.games=GAMES_INFO] - Array of game objects
 */
export default function GameGallery ({ games = GAMES_INFO }) {
  const { t } = useLanguage()
  const { isMobile } = useDeviceType()

  // Navigation State
  const [currentIndex, setCurrentIndex] = useState(0)

  // Calculate visible cards and navigation constraints
  const visibleCards = isMobile ? 1 : 2
  const totalGames = games.length
  const maxIndex = Math.max(0, totalGames - visibleCards)
  const showArrows = totalGames > visibleCards

  // Navigation handlers
  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, maxIndex))
  }

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0))
  }

  // Swipe gesture handlers (mobile only)
  const { handleTouchStart, handleTouchEnd } = useSwipe(
    handleNext, // onSwipeLeft -> next game
    handlePrev // onSwipeRight -> previous game
  )

  // Get games to display (slice for mobile, all for desktop)
  const displayedGames = isMobile
    ? [games[currentIndex]]
    : games.slice(currentIndex, currentIndex + visibleCards)

  // Check if navigation buttons should be disabled
  const isPrevDisabled = currentIndex === 0
  const isNextDisabled = currentIndex >= maxIndex

  return (
    <div className={styles.gameGallery}>
      {/* Label */}
      <label className={styles.label}>
        {t.home.gallery.select}
      </label>

      {/* Cards container */}
      <div
        className={styles.cards}
        onTouchStart={isMobile ? handleTouchStart : undefined}
        onTouchEnd={isMobile ? handleTouchEnd : undefined}
      >
        {displayedGames.map((game) => (
          <GameCard key={game.id} game={game} />
        ))}
      </div>

      {/* Navigation arrows - only show if needed */}
      {showArrows && (
        <div className={styles.controls}>
          <button
            type='button'
            onClick={handlePrev}
            disabled={isPrevDisabled}
            className={styles.arrow}
            aria-label={t.home.gallery.prev}
            title={t.home.gallery.prev}
          >
            <svg
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              aria-hidden='true'
            >
              <polyline points='15 18 9 12 15 6' />
            </svg>
          </button>

          <button
            type='button'
            onClick={handleNext}
            disabled={isNextDisabled}
            className={styles.arrow}
            aria-label={t.home.gallery.next}
            title={t.home.gallery.next}
          >
            <svg
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              aria-hidden='true'
            >
              <polyline points='9 18 15 12 9 6' />
            </svg>
          </button>
        </div>
      )}
    </div>
  )
}
